import { getTotalPages, getPageNumberList } from '../services/paginationService';
import paginationBar from './paginationBar';
import paginationRender from './paginationRender';

/**
 * Creates and initializes a pagination controller.
 * Handles rendering, navigation events, and URL generation.
 * @param {Object} params
 * @param {Object} params.selectors - CSS selectors for pagination DOM elements.
 * @param {string} params.selectors.optionList - Selector for the page numbers container.
 * @param {string} params.selectors.previousButton - Selector for the previous page button.
 * @param {string} params.selectors.nextButton - Selector for the next page button.
 * @param {string} params.selectors.pageTarget - Selector for clickable page elements.
 * @param {Object} params.callbacks - Callback functions for pagination actions.
 * @param {Function} params.callbacks.onPageSelect - Called when a page number is selected.
 * @param {Function} params.callbacks.onNext - Called when navigating to the next page.
 * @param {Function} params.callbacks.onPrevious - Called when navigating to the previous page.
 * @param {Function} params.callbacks.onPageSizeChange - Called when the page size is changed.
 * @param {Function} params.buildPageUrl - Builds a URL for a given page number.
 *
 * @returns {{ refresh: Function }} Pagination API with a refresh method.
 */
export default function createPagination({
  selectors,
  callbacks,
  buildPageUrl
}) {
  const {
    optionList,
    previousButton,
    nextButton,
    pageSize: pageSizeSelector,
  } = selectors;

  const {
    onPageSelect,
    onNext,
    onPrevious,
    onPageSizeChange
  } = callbacks;

  const optionListElement = document.querySelector(optionList);
  const previousButtonElement = document.querySelector(previousButton);
  const nextButtonElement = document.querySelector(nextButton);
  const pageSizeElement = document.querySelector(pageSizeSelector);

  /**
   * Re-renders the pagination based on the current state.
   * @param {number} totalResults - Total number of available results.
   * @param {number} pageSize - Number of results per page.
   * @param {number} currentPage - Currently active page.
   * @returns {void}
   */
  const refresh = (totalResults, pageSize, currentPage) => {
    const totalPages = getTotalPages(totalResults, pageSize);
    const pages = getPageNumberList(totalPages, currentPage);

    const paginationBarHtml = paginationBar({
      pages,
      currentPage,
      buildPageUrl
    });

    paginationRender({
      optionList: optionListElement,
      previousButton: previousButtonElement,
      nextButton: nextButtonElement,
      html: paginationBarHtml,
      currentPage,
      totalPages
    });
  };

  // Page number click handler (event delegation).
  optionListElement.addEventListener('click', (event) => {
    onPageSelect?.(event);
  });

  // Next page button click handler.
  nextButtonElement.addEventListener('click', (event) => {
    onNext?.(event);
  });

  // Previous page button click handler.
  previousButtonElement.addEventListener('click', (event) => {
    onPrevious?.(event);
  });

  pageSizeElement.addEventListener('change', (event) => {
    onPageSizeChange?.(event);
  });

  return { refresh };
}
